import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send, MessageCircle, Navigation } from "lucide-react";
import { FaInstagram, FaFacebookF } from "react-icons/fa";
import { CONTACT, activeCourses } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = ({ preselectCourse, showMap = true }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    course: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (preselectCourse) setForm((f) => ({ ...f, course: preselectCourse }));
  }, [preselectCourse]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    if (!consent) {
      toast.error("Devi accettare l'informativa sulla privacy");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, marketing });
      toast.success("Richiesta inviata! Ti ricontatteremo a breve.");
      setForm({ name: "", email: "", phone: "", course: "", message: "" });
      setConsent(false);
      setMarketing(false);
    } catch (err) {
      toast.error("Errore nell'invio. Riprova o scrivici su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-brand-black/20 py-3 text-brand-black placeholder:text-brand-ink/50 focus:outline-none focus:border-brand-red transition-colors";

  return (
    <section id="contatti" data-testid="contact-section" className="bg-brand-paper py-24 md:py-32 border-t border-border">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-14">
        {/* Left — big details */}
        <div className="lg:col-span-5">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-red mb-5">
            Contatti
          </div>
          <h2 className="font-heading font-bold text-4xl md:text-6xl tracking-tighter text-brand-black leading-[0.9] mb-10">
            Diamo forma alle tue idee.
          </h2>

          <div className="space-y-6">
            <a href={CONTACT.maps} target="_blank" rel="noreferrer" data-testid="contact-address" className="flex items-start gap-4 group">
              <MapPin className="text-brand-red mt-1 shrink-0" size={20} />
              <div>
                <div className="text-brand-black font-semibold group-hover:text-brand-red transition-colors">{CONTACT.venueAddress}</div>
                <div className="text-sm text-brand-ink">{CONTACT.venueCity}</div>
              </div>
            </a>
            <a href={`tel:${CONTACT.landline}`} data-testid="contact-landline" className="flex items-start gap-4 group">
              <Phone className="text-brand-red mt-1 shrink-0" size={20} />
              <div>
                <div className="text-brand-black font-semibold group-hover:text-brand-red transition-colors">{CONTACT.landlineDisplay}</div>
                <div className="text-sm text-brand-ink">Numero fisso</div>
              </div>
            </a>
            <a href={`tel:${CONTACT.phone}`} data-testid="contact-phone" className="flex items-start gap-4 group">
              <Phone className="text-brand-red mt-1 shrink-0" size={20} />
              <div>
                <div className="text-brand-black font-semibold group-hover:text-brand-red transition-colors">{CONTACT.phoneDisplay}</div>
                <div className="text-sm text-brand-ink">{CONTACT.owner}</div>
              </div>
            </a>
            <a href={`tel:${CONTACT.phone2}`} data-testid="contact-phone-2" className="flex items-start gap-4 group">
              <Phone className="text-brand-red mt-1 shrink-0" size={20} />
              <div>
                <div className="text-brand-black font-semibold group-hover:text-brand-red transition-colors">{CONTACT.phone2Display}</div>
                <div className="text-sm text-brand-ink">{CONTACT.owner2}</div>
              </div>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
              <Mail className="text-brand-red shrink-0" size={20} />
              <span className="text-brand-black font-semibold group-hover:text-brand-red transition-colors">{CONTACT.email}</span>
            </a>
            <div className="flex items-center gap-4">
              <Clock className="text-brand-red shrink-0" size={20} />
              <span className="text-brand-black font-semibold">{CONTACT.hours}</span>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={`https://wa.me/${CONTACT.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              data-testid="whatsapp-link"
              className="inline-flex items-center gap-2 bg-brand-black text-white text-sm font-bold uppercase tracking-widest px-6 py-3.5 hover:bg-brand-red transition-colors"
            >
              <MessageCircle size={18} /> WhatsApp
            </a>
            <a
              href={CONTACT.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="instagram-link"
              aria-label="Instagram"
              className="inline-flex items-center justify-center w-12 h-12 border border-brand-black text-brand-black hover:bg-brand-red hover:border-brand-red hover:text-white transition-colors"
            >
              <FaInstagram size={20} />
            </a>
            <a
              href={CONTACT.facebook}
              target="_blank"
              rel="noreferrer"
              data-testid="facebook-link"
              aria-label="Facebook"
              className="inline-flex items-center justify-center w-12 h-12 border border-brand-black text-brand-black hover:bg-brand-red hover:border-brand-red hover:text-white transition-colors"
            >
              <FaFacebookF size={18} />
            </a>
          </div>
        </div>

        {/* Right — form */}
        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-6 lg:col-start-7 bg-white border border-border p-8 md:p-10"
        >
          <div className="grid gap-6">
            <input data-testid="input-name" className={inputCls} placeholder="Nome e cognome *" value={form.name} onChange={update("name")} />
            <div className="grid sm:grid-cols-2 gap-6">
              <input data-testid="input-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={update("email")} />
              <input data-testid="input-phone" className={inputCls} placeholder="Telefono *" value={form.phone} onChange={update("phone")} />
            </div>
            <select data-testid="input-course" className={`${inputCls} appearance-none`} value={form.course} onChange={update("course")}>
              <option value="">Corso di interesse (opzionale)</option>
              <option value="Consulenza aziendale D.Lgs. 81/08">Consulenza aziendale D.Lgs. 81/08</option>
              <option value="Certificazione FGAS (persone e imprese)">Certificazione FGAS (persone e imprese)</option>
              <option value="Noleggio aule / coworking">Noleggio aule / coworking</option>
              <option value="Domiciliazione">Domiciliazione</option>
              {activeCourses().map((c) => (
                <option key={c.id} value={c.title}>{c.title}</option>
              ))}
            </select>
            <textarea data-testid="input-message" rows={4} className={`${inputCls} resize-none`} placeholder="Scrivi il tuo messaggio *" value={form.message} onChange={update("message")} />
            <label className="flex items-start gap-3 text-xs text-brand-ink cursor-pointer">
              <input
                type="checkbox"
                data-testid="contact-consent"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-brand-red shrink-0"
              />
              <span>
                Ho letto e accetto l'
                <Link to="/privacy" className="text-brand-red underline">informativa sulla privacy</Link>{" "}
                e acconsento al trattamento dei miei dati per essere ricontattato. *
              </span>
            </label>
            <label className="flex items-start gap-3 text-xs text-brand-ink cursor-pointer">
              <input
                type="checkbox"
                data-testid="contact-marketing"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-brand-red shrink-0"
              />
              <span>Acconsento a ricevere comunicazioni e novità sui corsi (facoltativo).</span>
            </label>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-form-submit"
              className="inline-flex items-center justify-center gap-2 bg-brand-red text-white font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-brand-black transition-colors disabled:opacity-60"
            >
              {loading ? "Invio in corso..." : <>Invia richiesta <Send size={16} /></>}
            </button>
          </div>
        </motion.form>
      </div>

      {showMap && (
      <div className="max-w-7xl mx-auto px-5 md:px-8 mt-14">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-5">
          <h3 className="font-heading font-black uppercase tracking-tighter text-3xl md:text-4xl text-brand-black">
            Dove <span className="text-brand-red">siamo</span>
          </h3>
          <a
            href={CONTACT.directions}
            target="_blank"
            rel="noreferrer"
            data-testid="directions-button"
            className="inline-flex items-center justify-center gap-2 bg-brand-red text-white font-bold uppercase tracking-widest text-sm px-7 py-4 hover:bg-brand-black transition-colors w-full sm:w-auto"
          >
            <Navigation size={18} /> Come arrivare
          </a>
        </div>
        <div className="relative overflow-hidden h-[420px] md:h-[540px] ring-4 ring-brand-red shadow-[0_24px_60px_-20px_rgba(214,40,40,0.4)]">
          <iframe
            title="Sede treffe — Viale Tevere 8/A, Frosinone"
            data-testid="contact-map"
            src="https://maps.google.com/maps?q=Viale%20Tevere%208%2FA%2C%2003100%20Frosinone%20FR&t=&z=16&ie=UTF8&iwloc=&output=embed"
            className="w-full h-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
      )}
    </section>
  );
};
